import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { Forbidden } from "./shares.js";
import { PrincipalMiddleware } from "./principal.js";
import { MeasureCategorySchema } from "./column-schemas.js";
/**
 * The caller's own client — module flags and measure categories the
 * employee/dashboard screens gate on. There is no by-id variant: a caller
 * with a `clientId` only ever reads their own (`Principal.clientId`), never
 * an arbitrary one.
 */
export const MyClient = S.Struct({
    id: S.UUID,
    name: S.NullOr(S.String),
    sifa_id: S.NullOr(S.UUID),
    ama_id: S.NullOr(S.UUID),
    /** clients.categories is a comma-separated string; [] when unset. */
    categories: S.Array(MeasureCategorySchema),
    measure_center_enabled: S.Boolean,
    online_courses_enabled: S.Boolean,
    documents_enabled: S.Boolean,
    /**
     * Brand identity the dashboard chrome keys on (SideNav logo and help-center
     * URLs). Legacy granted client-scoped roles an all-fields read on their own
     * clients row, so exposing these here stays narrower than the old grant.
     */
    contract_type: S.NullOr(S.String),
    /** yp_files id of the custom logo, if one is set. */
    logo: S.NullOr(S.UUID),
    /** The onboarding team-invite step's seat allocation for this client. */
    num_licenses_ohs_officers: S.NullOr(S.Int),
    num_licenses_location_managers: S.NullOr(S.Int),
});
export const GetMyClient = HttpApiEndpoint.get("getMyClient", "/client/me")
    .addSuccess(S.Struct({ data: MyClient }))
    .addError(Forbidden, {
    status: 403,
});
/**
 * Every module/sub-feature gate `clients.*_enabled` carries — the typed
 * replacement for the raw `clients` fetch `checkAccess` (apps/web
 * auth-helper) does today. `initial_audit_assign` is the one non-boolean
 * gate: which side owns the initial audit when the module is on.
 */
export const ClientModules = S.Struct({
    measure_center_enabled: S.Boolean,
    online_courses_enabled: S.Boolean,
    documents_enabled: S.Boolean,
    cockpit_enabled: S.Boolean,
    dearest_enabled: S.Boolean,
    insights_enabled: S.Boolean,
    risk_center_enabled: S.Boolean,
    examinations_enabled: S.Boolean,
    initial_audit_enabled: S.Boolean,
    notifications_enabled: S.Boolean,
    measure_center_assign_enabled: S.Boolean,
    online_courses_shares_enabled: S.Boolean,
    online_courses_assign_enabled: S.Boolean,
    documents_assign_enabled: S.Boolean,
    documents_share_enabled: S.Boolean,
    dearest_external_categories_enabled: S.Boolean,
    /** Officers share Dearest with employees, and each share grants access. */
    dearest_share_signup_enabled: S.Boolean,
    /** People sign up themselves with a work address under an active auth domain. */
    dearest_domain_signup_enabled: S.Boolean,
    dearest_booking_virtual_enabled: S.Boolean,
    dearest_booking_onsite_enabled: S.Boolean,
    hris_personio_enabled: S.Boolean,
    hris_sap_enabled: S.Boolean,
    initial_audit_assign: S.NullOr(S.Literal("internal", "external")),
});
export const GetClientModules = HttpApiEndpoint.get("getClientModules", "/client/modules")
    .addSuccess(S.Struct({ data: ClientModules }))
    .addError(Forbidden, { status: 403 });
export const ClientSeatHolder = S.Struct({
    id: S.UUID,
    first_name: S.NullOr(S.String),
    last_name: S.NullOr(S.String),
    email: S.String,
    role: S.NullOr(S.UUID),
});
export const GetMyClientSeatHolders = HttpApiEndpoint.get("getMyClientSeatHolders", "/client/me/seat-holders")
    .addSuccess(S.Struct({ data: S.Array(ClientSeatHolder) }))
    .addError(Forbidden, { status: 403 });
export const ownClientGroup = HttpApiGroup.make("client")
    .add(GetMyClient)
    .add(GetMyClientSeatHolders)
    .add(GetClientModules)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=own-client.js.map